import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SolicitacaoFormData } from '@/types/solicitacao';
import { useRequisicoes } from '@/hooks/useRequisicoes';
import { useCategorias } from '@/hooks/useCategorias';
import { useAuth } from '@/hooks/useAuth';
import { useClientContext } from '@/contexts/ClientContext';
import SolicitacaoFormFields from '../Solicitacoes/SolicitacaoFormFields';
import { FileUpload } from '@/components/ui/file-upload';
import { useSyncCategoriaDependentes } from "@/hooks/useSyncCategoriaDependentes";
import { NewRequisicaoActions } from "./NewRequisicaoActions";
import { DynamicCategoriaFields } from '../Tickets/DynamicCategoriaFields';

// Helper para validar conteúdo do RichTextEditor (que pode ter HTML vazio tipo <p></p>)
const isRichTextFilled = (val?: string) => {
  if (!val) return false;
  const stripped = val.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim();
  return stripped.length > 0;
};

const requisicaoSchema = z.object({
  titulo: z.string().min(1, 'Título é obrigatório'),
  descricao: z.string().refine(isRichTextFilled, { message: 'Conteúdo é obrigatório' }),
  tipo: z.literal('requisicao'),
  categoria_id: z.string().min(1, 'Categoria é obrigatória'),
  sla_id: z.string().optional(),
  urgencia: z.enum(['baixa', 'media', 'alta', 'critica']),
  impacto: z.enum(['baixo', 'medio', 'alto']),
  prioridade: z.enum(['baixa', 'media', 'alta', 'critica']),
  status: z.enum(['aberta', 'em_andamento', 'pendente', 'resolvida', 'fechada']),
  solicitante_id: z.string().min(1, 'Solicitante é obrigatório'),
  client_id: z.string().optional(),
  grupo_responsavel_id: z.string().min(1, 'Grupo Responsável é obrigatório'),
  atendente_id: z.string().min(1, 'Atendente é obrigatório'),
  canal_origem: z.enum(['portal', 'email', 'telefone', 'chat', 'presencial']),
  data_limite_resposta: z.string().optional(),
  data_limite_resolucao: z.string().optional(),
  origem_id: z.string().optional(),
  ativos_envolvidos: z.array(z.any()).optional(),
  notas_internas: z.string().optional(),
  tags: z.array(z.string()).optional(),
});

interface NewRequisicaoDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const NewRequisicaoDialog = ({ isOpen, onClose }: NewRequisicaoDialogProps) => {
  const { createRequisicao } = useRequisicoes();
  const { categorias } = useCategorias();
  const { user } = useAuth();
  const { currentClientId } = useClientContext();
  const [anexos, setAnexos] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState('info');

  const form = useForm<SolicitacaoFormData>({
    resolver: zodResolver(requisicaoSchema),
    defaultValues: {
      titulo: "",
      tipo: "requisicao",
      urgencia: "media",
      impacto: "medio",
      prioridade: "media",
      status: "aberta",
      canal_origem: "portal",
      solicitante_id: user?.id || "",
    },
  });

  // Preencher solicitante_id e client_id quando o usuário estiver disponível.
  // IMPORTANTE: só preencher client_id se ainda estiver vazio, para não
  // sobrescrever o cliente vindo da categoria selecionada (ver
  // useSyncCategoriaDependentes).
  useEffect(() => {
    if (user?.id && !form.getValues('solicitante_id')) {
      form.setValue('solicitante_id', user.id);
    }
    if (currentClientId && !form.getValues('client_id')) {
      form.setValue('client_id', currentClientId);
    }
  }, [user?.id, currentClientId, form]);

  // Sincronizar campos dependentes da categoria escolhida
  useSyncCategoriaDependentes(form, categorias);

  const categoriaId = form.watch('categoria_id');

  const onSubmit = async (data: SolicitacaoFormData) => {
    try {
      const dataWithAnexos = {
        ...data,
        anexos: anexos.length > 0 ? anexos.map((url) => ({ url, type: "file" })) : undefined,
      };

      await createRequisicao.mutateAsync(dataWithAnexos);
      form.reset();
      setAnexos([]);
      onClose();
    } catch (error) {
      console.error("Error creating requisição:", error);
    }
  };

  const handleClose = () => {
    form.reset();
    setAnexos([]);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" aria-describedby="nova-requisicao-description">
        <DialogHeader>
          <DialogTitle>Nova Requisição</DialogTitle>
        </DialogHeader>
        <div id="nova-requisicao-description" className="sr-only">
          Formulário para criar uma nova requisição no sistema
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="info">Informações</TabsTrigger>
                <TabsTrigger value="campos" disabled={!categoriaId}>
                  Campos Adicionais
                </TabsTrigger>
              </TabsList>

              <TabsContent value="info" className="space-y-4 mt-4">
                <SolicitacaoFormFields 
                  form={form} 
                  readOnlyFields={['data_limite_resposta', 'data_limite_resolucao']}
                  filteredCategorias={(categorias || []).filter(c => c.tipo === 'solicitacao' || c.tipo === 'requisicao')} 
                  slaAplicaA="solicitacao" 
                  userSelectMode="searchable" 
                />

                <FileUpload
                  onFilesChange={setAnexos}
                  maxFiles={5}
                  acceptedFileTypes="image/*,.pdf,.doc,.docx,.txt,.xlsx,.xls"
                  maxFileSize={10}
                />
              </TabsContent>

              <TabsContent value="campos" className="space-y-4 mt-4">
                <DynamicCategoriaFields form={form} categoriaId={categoriaId} />
              </TabsContent>
            </Tabs>

            <NewRequisicaoActions
              isLoading={createRequisicao.isPending}
              onCancel={handleClose}
            />
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
